import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import type {
  V2ConversationTurn,
  ContentBlock,
  LayoutActionBlock,
} from '@shared/conversation-schema';
import type { SnapshotAction } from '@shared/v2-schemas';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle } from 'lucide-react';

import { MessageMarkdown } from './MessageMarkdown';

import { cn } from '@/lib/utils';
import { createScrollDirectionState, resolveScrollDirection } from '@/lib/scrollDirection';
import { useAuth } from '@/hooks/useAuth';
import { WidgetUpdateSurface } from '@/components/v2/widgets/WidgetUpdateSurface';
import { ChatMessageBubble } from '@/components/v2/conversation/ChatMessageBubble';
import { ChatTurnAvatar } from '@/components/v2/conversation/ChatTurnAvatar';
import { ChatTypingDots } from '@/components/v2/conversation/ChatTypingDots';
import { ChatActionsCarousel } from '@/components/v2/conversation/ChatActionsCarousel';

export interface V2ConversationTranscriptProps {
  turns: V2ConversationTurn[];
  /** Turn processing, no content yet — shows the "Anaka is thinking" indicator. */
  isThinking: boolean;
  /**
   * Turn in flight with content arriving — shows the same dots indicator
   * relabelled "Anaka is responding". Phase that follows `isThinking`.
   */
  isResponding?: boolean;
  /**
   * @deprecated Text-mode `send_status_update` now renders in the input box
   * (`ConversationInputBar` → `TextZone`), not the transcript — the status
   * lives where the user's attention is after they hit send, and is no longer
   * duplicated beside the typing dots. Prop retained (accepted + ignored) for
   * back-compat with existing callers; the transcript shows only the typing
   * dots while thinking.
   */
  textStatusUpdate?: { text: string; label: string } | null;
  onAction?: (
    widgetId: string,
    action: string,
    payload?: Record<string, unknown>,
    meta?: { text?: string; label?: string },
  ) => void;
  onLayoutAction?: (block: LayoutActionBlock) => void;
  className?: string;
  /**
   * Visual treatment for assistant text bubbles. `'white'` paints the
   * bubble `#FFFFFF` with dark text — used in focused mode where the
   * thread surface itself is transparent. Defaults to `'glass'`.
   */
  assistantBubbleSurface?: 'glass' | 'white';
  /** Called when a next-action pill sends a message in-place. */
  onSendMessage?: (text: string) => void;
  /** Called when a next-action pill navigates without sending. */
  onNavigate?: (href: string) => void;
  /** Called when a next-action pill navigates and sends. */
  onNavigateAndSend?: (href: string, text: string) => void;
  /** Called when a next-action pill triggers a widget action. */
  onWidgetAction?: (args: { widget_id: string; action: string; text?: string }) => void;
  /** Suppress next-action pills in voice mode — pills are text-only affordances. */
  inputMode?: 'text' | 'voice';
  /** Shell display mode — pills are hidden in peek mode (collapsed surface). */
  shellMode?: 'peek' | 'detailed' | 'focused';
  /** ANA-294 / matrix B2 — gate next-action pills to home/update; ChatThread
   *  passes false in scoping/demo/interview. Defaults true (back-compat). */
  allowActionPills?: boolean;
  /**
   * Fires 'down'/'up' once the thread's cumulative scroll movement in that
   * direction crosses a small threshold (never on the tiniest jitter), and
   * always fires 'up' once scrolled fully to the top. Lets a page-level
   * chrome (e.g. the scoping mobile nav) auto-hide/reveal in step with the
   * thread, without this component knowing anything about that chrome.
   */
  onScrollDirection?: (direction: 'up' | 'down') => void;
}

/** Cumulative scroll movement (px) before onScrollDirection fires — small
 *  jitters and per-frame smooth-scroll increments don't each cross this. */
const SCROLL_DIRECTION_THRESHOLD = 24;

/** How close (px) to the true bottom still counts as "at the bottom" for deciding
 *  whether to auto-scroll on a new turn/status change — a little slack so the
 *  user isn't stranded just above the edge. */
const STICK_TO_BOTTOM_THRESHOLD = 48;

interface GroupedTurn {
  turn_id: string;
  role: 'user' | 'assistant';
  content: ContentBlock[];
  /** Earliest timestamp seen for the group — drives the meta line + separator. */
  timestamp: string;
  /** Per-turn next-action pills from the gateway, carried from the last source turn in the group. */
  actions?: SnapshotAction[];
}

/** Stable idempotency key so layout actions are not re-dispatched when `turns` is extended or re-created. */
function layoutActionDispatchKey(
  turn: V2ConversationTurn,
  blockIndex: number,
  block: LayoutActionBlock,
): string {
  const params =
    block.params === undefined
      ? ''
      : JSON.stringify(block.params, Object.keys(block.params).sort());
  return `${turn.turn_id}\0${turn.role}\0${turn.timestamp}\0${blockIndex}\0${block.widget_id}\0${block.action}\0${params}`;
}

/** @internal Exported for unit testing — do not import from application code. */
export function groupTurns(turns: V2ConversationTurn[]): GroupedTurn[] {
  const grouped: GroupedTurn[] = [];
  for (const turn of turns) {
    const last = grouped[grouped.length - 1];
    if (last && last.turn_id === turn.turn_id && last.role === turn.role) {
      // Replace the item rather than mutating it so external references and
      // identity-based memoization downstream see a stable object (ANA-228).
      grouped[grouped.length - 1] = {
        ...last,
        content: [...last.content, ...turn.content],
        // Carry actions from the latest turn in the group (attachActionsToLastAssistantTurn
        // replaces the last item in the array, so the latest view has the most up-to-date actions).
        ...(turn.actions !== undefined ? { actions: turn.actions } : {}),
      };
      continue;
    }
    grouped.push({
      turn_id: turn.turn_id,
      role: turn.role,
      content: [...turn.content],
      timestamp: turn.timestamp,
      actions: turn.actions,
    });
  }
  return grouped;
}

/** Compact "10:42" / "just now" timestamp for the meta line. */
function formatTurnTime(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const diffMs = now.getTime() - date.getTime();
  if (diffMs >= 0 && diffMs < 60_000) return 'just now';

  return date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Append ?from=chat so ConversationShell opens in focused mode on landing. */
function withChatNavParam(href: string): string {
  return href.includes('?') ? `${href}&from=chat` : `${href}?from=chat`;
}

/**
 * Stable React key for a content block within a turn. Widgets key by their
 * `widget_id` so a re-emitted card (voice dedup swaps the surviving instance for
 * a fresh `widget_id`) mounts as a new element and plays its update animation,
 * instead of an index key silently reusing the DOM node in place. Text/layout
 * blocks have no id, so they fall back to a positional key scoped to the turn —
 * safe because grouped turn content is append-only.
 */
function contentBlockKey(turnId: string, block: ContentBlock, idx: number): string {
  if (block.type === 'widget') return `w:${block.widget_id}`;
  return `${turnId}:${block.type}:${idx}`;
}

/**
 * Text mode — Anaka's spoken/typed reply renders BELOW her widget cards, not
 * interleaved in raw arrival order (a card can stream in after its accompanying
 * text, or vice versa, depending on the SSE delivery path). Stable-partitions a
 * turn's content: every `widget` block first (relative order preserved), then
 * everything else (relative order preserved). `layout_action` blocks render
 * nothing (see `ContentBlockRenderer`), so their position is immaterial.
 *
 * Voice mode is untouched — deliberately called only for `inputMode === 'text'`
 * assistant turns at the call site, since voice doesn't render a text transcript
 * the same way and user turns never carry widgets.
 */
function orderContentForTextDisplay(content: ContentBlock[]): ContentBlock[] {
  const widgets = content.filter((block) => block.type === 'widget');
  const rest = content.filter((block) => block.type !== 'widget');
  return [...widgets, ...rest];
}

export const V2ConversationTranscript: React.FC<V2ConversationTranscriptProps> = ({
  turns,
  isThinking,
  isResponding = false,
  onAction,
  onLayoutAction,
  className,
  assistantBubbleSurface = 'glass',
  onSendMessage,
  onNavigate,
  onNavigateAndSend,
  onWidgetAction,
  inputMode = 'text',
  shellMode,
  allowActionPills = true,
  onScrollDirection,
}) => {
  const { user } = useAuth();
  const scrollRef = useRef<HTMLDivElement>(null);
  const dispatchedLayoutActionKeysRef = useRef<Set<string>>(new Set());
  // Baseline for cumulative-delta scroll-direction detection — shared with
  // NavBar's window-scroll hide via `resolveScrollDirection`, so both hide/show
  // mechanisms run the same tested logic instead of two independent copies.
  const scrollDirectionStateRef = useRef(createScrollDirectionState());
  // Sticky-bottom tracking (independent of onScrollDirection/scoping — applies to
  // every mode). Starts `true` so the initial mount/first turns still auto-scroll.
  // During a live call, `isThinking`/`isResponding` flip continuously; without this
  // gate, a user who scrolled UP to read history got yanked back to the bottom on
  // the very next status change ("can't scroll up, it bounces").
  const isAtBottomRef = useRef(true);
  // Last-seen turn id — lets the effect tell "a NEW turn was just appended" apart
  // from "the same turn is still sitting there while isThinking/isResponding
  // cycles" (P2, PR #1223 review). Only the former should override the
  // stick-to-bottom gate.
  const lastTurnIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!scrollRef.current) return;
    const lastTurn = turns[turns.length - 1];
    const isNewTurn = (lastTurn?.turn_id ?? null) !== lastTurnIdRef.current;
    lastTurnIdRef.current = lastTurn?.turn_id ?? null;

    // The user just sent something (text or voice) — a strong intent to follow
    // the conversation, so re-anchor to the bottom even if they'd scrolled up to
    // read history, and keep following through the reply that follows it.
    if (isNewTurn && lastTurn?.role === 'user') {
      isAtBottomRef.current = true;
    }

    if (isAtBottomRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [turns, isThinking, isResponding]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      isAtBottomRef.current = distanceFromBottom <= STICK_TO_BOTTOM_THRESHOLD;

      if (!onScrollDirection) return;
      const direction = resolveScrollDirection(el.scrollTop, scrollDirectionStateRef.current, SCROLL_DIRECTION_THRESHOLD);
      if (direction) onScrollDirection(direction);
    },
    [onScrollDirection],
  );

  useEffect(() => {
    if (!onLayoutAction) return;
    for (let t = 0; t < turns.length; t++) {
      const turn = turns[t];
      for (let i = 0; i < turn.content.length; i++) {
        const block = turn.content[i];
        if (block.type !== 'layout_action') continue;
        const key = layoutActionDispatchKey(turn, i, block);
        if (dispatchedLayoutActionKeysRef.current.has(key)) continue;
        dispatchedLayoutActionKeysRef.current.add(key);
        onLayoutAction(block);
      }
    }
  }, [turns, onLayoutAction]);

  const groupedTurns = useMemo(() => groupTurns(turns), [turns]);

  const lastAssistantGroupIndex = useMemo(() => {
    for (let i = groupedTurns.length - 1; i >= 0; i--) {
      if (groupedTurns[i].role === 'assistant') return i;
    }
    return -1;
  }, [groupedTurns]);

  // Pills hide once the user has replied — spec 013 US1 scenario 3.
  const transcriptEndsWithAssistant =
    groupedTurns.length > 0 && groupedTurns[groupedTurns.length - 1].role === 'assistant';

  const userFirstName = user?.full_name?.split(' ')[0] || 'You';
  const userInitial = user?.full_name?.[0] || 'U';
  const userAvatar = user?.avatar_url ?? null;

  if (groupedTurns.length === 0 && !isThinking && !isResponding) {
    return (
      <div
        className={cn(
          'flex flex-1 flex-col items-center justify-center space-y-4 p-8 text-muted-foreground',
          className,
        )}
      >
        <MessageCircle className="h-12 w-12 opacity-20" />
        <p className="text-sm font-medium uppercase tracking-widest opacity-50">
          No messages yet
        </p>
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      data-testid="conversation-transcript-scroll"
      onScroll={handleScroll}
      className={cn(
        // `justify-end-safe` bottom-anchors the thread but falls back to start
        // alignment when turns overflow the viewport — plain `justify-end` clips
        // the earliest messages above the scroll origin, making scroll-up
        // impossible in Chrome/Safari (the input bar is a separate sibling, so
        // it stays fixed regardless).
        'flex flex-1 flex-col justify-end-safe gap-4 overflow-y-auto scrollbar-glass scroll-smooth px-4 py-5 @[600px]:px-6',
        className,
      )}
    >
      <AnimatePresence initial={false}>
        {groupedTurns.map((turn, groupIndex) => {
          const isAgent = turn.role === 'assistant';
          const showActions =
            isAgent &&
            groupIndex === lastAssistantGroupIndex &&
            transcriptEndsWithAssistant &&
            !isThinking &&
            inputMode !== 'voice' &&
            shellMode !== 'peek' &&
            allowActionPills &&
            turn.actions &&
            turn.actions.length > 0;

          return (
            <React.Fragment key={turn.turn_id}>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.18 }}
                className={cn(
                  'flex gap-3',
                  isAgent
                    ? 'flex-row self-stretch'
                    : 'flex-row-reverse self-stretch',
                )}
              >
                <ChatTurnAvatar
                  role={turn.role}
                  initial={isAgent ? 'A' : userInitial}
                  src={isAgent ? null : userAvatar}
                />

                <div
                  className={cn(
                    'flex min-w-0 flex-1 flex-col gap-1',
                    isAgent ? 'items-start' : 'items-end',
                  )}
                >
                  <div
                    className={cn(
                      'flex items-baseline gap-2',
                      isAgent ? 'flex-row' : 'flex-row-reverse',
                    )}
                  >
                    <span
                      className={cn(
                        'text-[12px] font-semibold',
                        isAgent ? '' : 'text-foreground',
                      )}
                      style={isAgent ? { color: '#7C3AED' } : undefined}
                    >
                      {isAgent ? 'Anaka' : userFirstName}
                    </span>
                    <span
                      className="text-[10.5px] font-mono"
                      style={{ color: 'var(--foreground-subtle, #6B7280)' }}
                    >
                      {formatTurnTime(turn.timestamp)}
                    </span>
                  </div>

                  {/* Content blocks — text bubbles + widget cards — get real
                      separation from each other so a widget has breathing room
                      from the message it accompanies (and from stacked widgets).
                      Kept in their own column so the parent's gap-1 still holds
                      the meta label tight to the first block. `w-full` + the
                      shared alignment keep bubbles/cards on the correct side. */}
                  <div
                    className={cn(
                      'flex w-full flex-col gap-3',
                      isAgent ? 'items-start' : 'items-end',
                    )}
                  >
                    {(isAgent && inputMode === 'text'
                      ? orderContentForTextDisplay(turn.content)
                      : turn.content
                    ).map((block, idx) => (
                      <ContentBlockRenderer
                        key={contentBlockKey(turn.turn_id, block, idx)}
                        block={block}
                        isAgent={isAgent}
                        onAction={onAction}
                        assistantBubbleSurface={assistantBubbleSurface}
                      />
                    ))}
                  </div>

                  {showActions && (
                    <ChatActionsCarousel
                      actions={turn.actions!}
                      variant="outlined"
                      onSendMessage={onSendMessage ?? (() => {})}
                      onNavigate={(href) => onNavigate?.(withChatNavParam(href))}
                      onNavigateAndSend={(href, text) => onNavigateAndSend?.(withChatNavParam(href), text)}
                      onWidgetAction={onWidgetAction ?? (() => {})}
                    />
                  )}
                </div>
              </motion.div>
            </React.Fragment>
          );
        })}
      </AnimatePresence>

      {(isThinking || isResponding) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex gap-3"
          data-testid="chat-thread-thinking"
          data-status={isResponding ? 'responding' : 'thinking'}
        >
          <ChatTurnAvatar role="assistant" />
          <div className="flex min-w-0 flex-1 flex-col gap-1 items-start">
            <div className="flex items-baseline gap-2">
              <span className="text-[12px] font-semibold" style={{ color: '#7C3AED' }}>
                Anaka
              </span>
            </div>
            <ChatMessageBubble role="assistant" surface={assistantBubbleSurface}>
              <span className="inline-flex items-center gap-2">
                <ChatTypingDots
                  label={isResponding ? 'Anaka is responding' : 'Anaka is thinking'}
                />
              </span>
            </ChatMessageBubble>
          </div>
        </motion.div>
      )}
    </div>
  );
};

interface ContentBlockRendererProps {
  block: ContentBlock;
  isAgent: boolean;
  onAction?: (
    widgetId: string,
    action: string,
    payload?: Record<string, unknown>,
    meta?: { text?: string; label?: string },
  ) => void;
  assistantBubbleSurface?: 'glass' | 'white';
}

function ContentBlockRenderer({
  block,
  isAgent,
  onAction,
  assistantBubbleSurface = 'glass',
}: ContentBlockRendererProps) {
  switch (block.type) {
    case 'text':
      return (
        <ChatMessageBubble
          role={isAgent ? 'assistant' : 'user'}
          surface={isAgent ? assistantBubbleSurface : undefined}
        >
          <MessageMarkdown>{block.text}</MessageMarkdown>
        </ChatMessageBubble>
      );
    case 'widget':
      // WidgetUpdateSurface owns the one-shot mount pop+glow (a new / moved /
      // fresh-id card that genuinely remounts) AND the in-place skeleton
      // "refresh" for a Cloud-materialized card re-emitted under a STABLE
      // `sc-card:` id (004-p6 replace-by-widget_id) — which keeps the same
      // React key and so never remounts. See that component for the full contract.
      return <WidgetUpdateSurface block={block} onAction={onAction} />;
    case 'layout_action':
      return null;
  }
}
